import { type JSX } from 'react';
import { ContextKindKey } from '@gonfalon/contexts/types';
import { enableNewReviewSaveModal } from '@gonfalon/dogfood-flags/dogfood-flags';
import { List } from '@gonfalon/immutable-shim/immutableShim';
import { toFlagTargetingConflict, toFlagVariationConflict, toHref } from '@gonfalon/navigator/navigators';
import { capitalize } from '@gonfalon/strings/capitalize';
import { stringifyValue } from '@gonfalon/types/stringifyValue';
import { Alert } from '@launchpad-ui/components';
import clsx from 'clsx';

import { ExpiringTargetsInstructionEntry } from '#components/InstructionList/ExpiringUserTargetsInstructionEntry';
import { PrerequisiteEntryContainer } from '#components/InstructionList/PrerequisiteEntryContainer';
import {
  RuleDescriptionKind,
  RuleInstructionDescription,
} from '#components/InstructionList/RuleInstructionDescription';
import { OnOffChip } from '#components/OnOffChip';
import { Clause, createClause } from '#utils/clause/records/Clause';
import type { ContextTargetingExpirationInstruction } from '#utils/expiringContextTargetsUtils';
import type { Flag } from '#utils/flags/records/Flag';
import type { Rule } from '#utils/flags/records/Rule';
import { replaceRuleLabelText } from '#utils/flags/replaceRuleLabelText';
import { AutomatedReleaseInstructionKind } from '#utils/instructions/automated-release/types';
import { ClauseInstructionKind } from '#utils/instructions/clauses/constants';
import { ExpiringTargetsInstructionKind } from '#utils/instructions/expiringTargets/types';
import { OnOffInstructionKind } from '#utils/instructions/onOff/types';
import { FlagPrerequisitesInstructionKind } from '#utils/instructions/prerequisites/types';
import { ProgressiveRolloutInstructionKind } from '#utils/instructions/progressive-rollouts/types';
import { RuleInstructionKind } from '#utils/instructions/rules/constants';
import { convertRuleToAggregateRule } from '#utils/instructions/rules/convertRuleToAggregateRule';
import { createAggregateRule } from '#utils/instructions/rules/createAggregateRule';
import { filterOutAggregateRules } from '#utils/instructions/rules/filterOutAggregateRules';
import { sortRulesByTargetingOrder } from '#utils/instructions/rules/sortRulesByTargetingOrder';
import type { AggregateRule } from '#utils/instructions/rules/types';
import { getInstructionDescriptionText } from '#utils/instructions/shared/getInstructionDescriptionText';
import { isInstructionOfKind } from '#utils/instructions/shared/isInstructionOfKind';
import type { InstructionIndexToConflictsInfo, SemanticInstruction } from '#utils/instructions/shared/types';
import { hasEmptyTargets } from '#utils/instructions/targets/helpers';
import { TargetsInstructionKind } from '#utils/instructions/targets/types';
import { hasEmptyUserTargets } from '#utils/instructions/userTargets/helpers';
import { UserTargetsInstructionKind } from '#utils/instructions/userTargets/types';
import type { AddVariationSemanticInstruction } from '#utils/instructions/variations/types';
import { FlagVariationsInstructionKind } from '#utils/instructions/variations/types';

import { AddVariationInstructionEntry } from './AddVariationInstructionEntry';
import { ClauseValueInstructionEntry } from './ClauseValueInstructionEntry';
import { CollapsibleInstructionListItem } from './CollapsibleInstructionListItem';
import type { VariationInfo } from './DefaultVariationInstructionEntry';
import { DefaultVariationInstructionEntry } from './DefaultVariationInstructionEntry';
import { InstructionListItem } from './InstructionListItem';
import {
  FlagInstructionKindToInstructionListCategory,
  InstructionListCategory,
  InstructionListChangeKind,
  InstructionListSubCategory,
  makeInstructionElemsByCategory,
  makeInstructionElemsByRuleId,
  RULE_NOT_FOUND,
  TARGET_DISPLAY_LIMIT,
} from './instructionListUtils';
import { MeasuredRolloutRuleInstructionDescription } from './MeasuredRolloutRuleInstructionDescription';
import { NestedInstructionListItem } from './NestedInstructionListItem';
import { ProgressiveRolloutConfigurationDescription } from './ProgressiveRolloutConfigurationDescription';
import { RemoveVariationInstructionEntry } from './RemoveVariationInstructionEntry';
import { StartAutomatedReleaseInstructionEntry } from './StartAutomatedReleaseInstructionEntry';
import { StopProgressiveRolloutInstructionEntry } from './StopProgressiveRolloutInstructionEntry';
import { TargetEntryContainer } from './TargetEntryContainer';
import { UpdateVariationInstructionEntry } from './UpdateVariationInstructionEntry';
import { VariationInstructionEntry } from './VariationInstructionEntry';
import {
  StopMeasuredRolloutInstructionEntry,
  VariationOrRolloutInstructionEntry,
} from './VariationOrRolloutInstructionEntry';

import defaultVariationStyles from './DefaultVariationInstructionEntry.module.css';
import styles from './styles.module.css';

export type CollapsibleInstructionListProps = {
  instructions: Array<SemanticInstruction | undefined>;
  flag: Flag;
  projKey: string;
  envKey: string;
  expandTargetList?: boolean;
  shouldShowConflictLinks?: boolean;
  hideCategoryHeaders?: boolean;
  onExit?: () => void;
  isApprovalRequest?: boolean;
  initialOpen?: boolean;
  collapseState?: Record<string, boolean | undefined>;
  setCollapseState?: (id: string, open: boolean) => void;
  instructionIndexToConflictsInfo?: InstructionIndexToConflictsInfo;
};

export function CollapsibleInstructionList({
  instructions,
  flag,
  projKey,
  envKey,
  expandTargetList,
  shouldShowConflictLinks,
  hideCategoryHeaders,
  onExit,
  isApprovalRequest,
  instructionIndexToConflictsInfo,
  initialOpen,
  collapseState,
  setCollapseState,
}: CollapsibleInstructionListProps) {
  const instructionElemsByCategory = makeInstructionElemsByCategory();
  const filteredIns = instructions.filter((ins): ins is SemanticInstruction => !!ins);
  const editRuleInstructionElemsByRuleId = makeInstructionElemsByRuleId(filteredIns);
  const sortedRules = sortRulesByTargetingOrder({ flag, envKey, instructions: filteredIns });
  const ruleLabel = flag.getRuleLabel();
  const categoriesWithConflicts: Partial<Record<InstructionListCategory, boolean>> = {};

  const renderInstructionsWithCategories = (categoriesToExpand: string[] = []) => {
    const categoriesWithInstructions = Object.keys(instructionElemsByCategory).filter(
      (category) => instructionElemsByCategory[category as InstructionListCategory].length,
    );
    return categoriesWithInstructions.map((category, index) => {
      const categoryInsElems = instructionElemsByCategory[category as InstructionListCategory];
      const isCategoryExpanded = enableNewReviewSaveModal()
        ? (initialOpen ?? true)
        : isApprovalRequest || index === 0 || categoriesToExpand.includes(category);
      return (
        <CollapsibleInstructionListItem
          key={category}
          hideCategoryHeaders={hideCategoryHeaders}
          initialOpen={isCategoryExpanded}
          categoryHeader={replaceRuleLabelText(category, ruleLabel)}
          categoryInsElems={categoryInsElems}
          open={collapseState?.[category] ?? isCategoryExpanded}
          onOpenChange={(open) => setCollapseState?.(category, open)}
        />
      );
    });
  };

  const categorizeInstruction = ({
    category,
    insElem,
    shouldAddElemToFront,
    hasConflict,
  }: {
    category: InstructionListCategory;
    insElem: JSX.Element;
    shouldAddElemToFront?: boolean;
    hasConflict?: boolean;
  }) => {
    categoriesWithConflicts[category] = hasConflict || categoriesWithConflicts[category];
    shouldAddElemToFront
      ? instructionElemsByCategory[category].unshift(insElem)
      : instructionElemsByCategory[category].push(insElem);
  };

  const categorizeEditRuleInstruction = ({
    ruleId,
    insElem,
    hasConflict,
  }: {
    ruleId: string;
    insElem: JSX.Element;
    hasConflict: boolean;
  }) => {
    categoriesWithConflicts[InstructionListCategory.RULES] =
      hasConflict || categoriesWithConflicts[InstructionListCategory.RULES];
    if (!editRuleInstructionElemsByRuleId[ruleId]) {
      editRuleInstructionElemsByRuleId[ruleId] = [];
    }
    editRuleInstructionElemsByRuleId[ruleId].push(insElem);
  };

  const categoriesToExpand: string[] = [];
  instructions.forEach((semanticInstruction, instructionIndex) => {
    if (!semanticInstruction) {
      return;
    }
    const conflict = instructionIndexToConflictsInfo?.[instructionIndex];
    const hasConflict = !!conflict;

    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const flagTab = instructions
      .filter(Boolean)
      .every((ins) => Object.values(FlagVariationsInstructionKind).includes(ins!.kind as FlagVariationsInstructionKind))
      ? 'variations'
      : 'targeting'; /* eslint-enable @typescript-eslint/no-non-null-assertion */
    const linker = flagTab === 'targeting' ? toFlagTargetingConflict : toFlagVariationConflict;
    const conflictLink = shouldShowConflictLinks
      ? toHref(
          linker({
            projectKey: projKey,
            environmentKey: envKey,
            flagKey: flag.key,
            conflictIndex: String(instructionIndex),
          }),
        )
      : undefined;
    const description = getInstructionDescriptionText(semanticInstruction, ruleLabel);
    const ruleId =
      filterOutAggregateRules(semanticInstruction.kind) || semanticInstruction.kind === RuleInstructionKind.REMOVE_RULE
        ? (semanticInstruction as AggregateRule).ruleId
        : '';

    switch (semanticInstruction.kind) {
      case OnOffInstructionKind.TURN_FLAG_ON:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              className={styles.inlineInstructionListItem}
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              <OnOffChip isOn />
            </InstructionListItem>
          ),
        });
      case OnOffInstructionKind.TURN_FLAG_OFF:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              className={styles.inlineInstructionListItem}
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              <OnOffChip isOn={false} />
            </InstructionListItem>
          ),
        });
      case OnOffInstructionKind.ENABLE_TARGETING:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              className={styles.inlineInstructionListItem}
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              <OnOffChip isOn />
            </InstructionListItem>
          ),
        });
      case OnOffInstructionKind.DISABLE_TARGETING:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              className={styles.inlineInstructionListItem}
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              <OnOffChip isOn={false} />
            </InstructionListItem>
          ),
        });
      case FlagPrerequisitesInstructionKind.ADD_PREREQUISITE:
      case FlagPrerequisitesInstructionKind.UPDATE_PREREQUISITE:
      case FlagPrerequisitesInstructionKind.REMOVE_PREREQUISITE:
        let prerequisteChangeKind;
        if (semanticInstruction.kind === FlagPrerequisitesInstructionKind.ADD_PREREQUISITE) {
          prerequisteChangeKind = InstructionListChangeKind.ADD;
        } else if (semanticInstruction.kind === FlagPrerequisitesInstructionKind.UPDATE_PREREQUISITE) {
          prerequisteChangeKind = InstructionListChangeKind.CHANGE;
        } else if (semanticInstruction.kind === FlagPrerequisitesInstructionKind.REMOVE_PREREQUISITE) {
          prerequisteChangeKind = InstructionListChangeKind.REMOVE;
        }
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={prerequisteChangeKind}
              onExit={onExit}
              conflict={conflict}
            >
              <PrerequisiteEntryContainer
                instructionKind={semanticInstruction.kind}
                flagKey={semanticInstruction.key}
                projKey={projKey}
                envKey={envKey}
                variationId={
                  isInstructionOfKind(semanticInstruction, FlagPrerequisitesInstructionKind.REMOVE_PREREQUISITE)
                    ? undefined
                    : semanticInstruction?.variationId
                }
              />
            </InstructionListItem>
          ),
        });
      case FlagPrerequisitesInstructionKind.REPLACE_PREREQUISITES:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              {semanticInstruction.prerequisites.length > 0 ? (
                <ul className={styles.instructionItemSubList}>
                  {semanticInstruction.prerequisites.map((prereq, i) => (
                    <li key={i}>
                      <PrerequisiteEntryContainer
                        instructionKind={semanticInstruction.kind}
                        flagKey={prereq.key}
                        projKey={projKey}
                        envKey={envKey}
                        variationId={prereq.variationId}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </InstructionListItem>
          ),
        });
      case UserTargetsInstructionKind.ADD_USER_TARGETS:
      case UserTargetsInstructionKind.REMOVE_USER_TARGETS:
        let userTargetsChangeKind;
        if (semanticInstruction.kind === UserTargetsInstructionKind.ADD_USER_TARGETS) {
          userTargetsChangeKind = InstructionListChangeKind.ADD;
        } else if (semanticInstruction.kind === UserTargetsInstructionKind.REMOVE_USER_TARGETS) {
          userTargetsChangeKind = InstructionListChangeKind.REMOVE;
        }
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={userTargetsChangeKind}
              onExit={onExit}
              conflict={conflict}
            >
              <TargetEntryContainer
                instructionKind={semanticInstruction.kind}
                contextKind={ContextKindKey.USER}
                targetKeys={semanticInstruction.values}
                variationId={semanticInstruction.variationId}
                flag={flag}
                targetLimit={expandTargetList ? 0 : TARGET_DISPLAY_LIMIT}
                projKey={projKey}
                envKey={envKey}
              />
            </InstructionListItem>
          ),
        });
      case UserTargetsInstructionKind.REPLACE_USER_TARGETS:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              {!hasEmptyUserTargets(semanticInstruction) ? (
                <ul className={styles.instructionItemSubList}>
                  {semanticInstruction.targets.map((v, i) => (
                    <li key={i}>
                      <TargetEntryContainer
                        instructionKind={semanticInstruction.kind}
                        contextKind={ContextKindKey.USER}
                        targetKeys={v.values}
                        variationId={v.variationId}
                        flag={flag}
                        targetLimit={expandTargetList ? 0 : TARGET_DISPLAY_LIMIT}
                        projKey={projKey}
                        envKey={envKey}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </InstructionListItem>
          ),
        });
      case TargetsInstructionKind.ADD_TARGETS:
      case TargetsInstructionKind.REMOVE_TARGETS:
        let targetsChangeKind;
        if (semanticInstruction.kind === TargetsInstructionKind.ADD_TARGETS) {
          targetsChangeKind = InstructionListChangeKind.ADD;
        } else if (semanticInstruction.kind === TargetsInstructionKind.REMOVE_TARGETS) {
          targetsChangeKind = InstructionListChangeKind.REMOVE;
        }
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={targetsChangeKind}
              onExit={onExit}
              conflict={conflict}
            >
              <TargetEntryContainer
                instructionKind={semanticInstruction.kind}
                contextKind={semanticInstruction.contextKind}
                targetKeys={semanticInstruction.values}
                variationId={semanticInstruction.variationId}
                flag={flag}
                targetLimit={expandTargetList ? 0 : TARGET_DISPLAY_LIMIT}
                projKey={projKey}
                envKey={envKey}
              />
            </InstructionListItem>
          ),
        });
      case TargetsInstructionKind.REPLACE_TARGETS:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              {!hasEmptyTargets(semanticInstruction) ? (
                <ul className={styles.instructionItemSubList}>
                  {semanticInstruction.targets.map((v, i) => (
                    <li key={i}>
                      <TargetEntryContainer
                        instructionKind={semanticInstruction.kind}
                        contextKind={v.contextKind}
                        targetKeys={v.values}
                        variationId={v.variationId}
                        flag={flag}
                        targetLimit={expandTargetList ? 0 : TARGET_DISPLAY_LIMIT}
                        projKey={projKey}
                        envKey={envKey}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </InstructionListItem>
          ),
        });
      case ExpiringTargetsInstructionKind.UPDATE_EXPIRE_USER_TARGET_DATE:
      case ExpiringTargetsInstructionKind.ADD_EXPIRE_USER_TARGET_DATE:
      case ExpiringTargetsInstructionKind.REMOVE_EXPIRE_USER_TARGET_DATE:
        let expiringTargetsChangeKind;
        if (semanticInstruction.kind === ExpiringTargetsInstructionKind.UPDATE_EXPIRE_USER_TARGET_DATE) {
          expiringTargetsChangeKind = InstructionListChangeKind.CHANGE;
        } else if (semanticInstruction.kind === ExpiringTargetsInstructionKind.ADD_EXPIRE_USER_TARGET_DATE) {
          expiringTargetsChangeKind = InstructionListChangeKind.ADD;
        } else if (semanticInstruction.kind === ExpiringTargetsInstructionKind.REMOVE_EXPIRE_USER_TARGET_DATE) {
          expiringTargetsChangeKind = InstructionListChangeKind.REMOVE;
        }
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={expiringTargetsChangeKind}
              onExit={onExit}
              conflict={conflict}
            >
              <ExpiringTargetsInstructionEntry
                expiringIns={semanticInstruction as unknown as ContextTargetingExpirationInstruction}
                projKey={projKey}
              />
            </InstructionListItem>
          ),
        });

      case ExpiringTargetsInstructionKind.UPDATE_EXPIRE_TARGET_DATE:
      case ExpiringTargetsInstructionKind.ADD_EXPIRE_TARGET_DATE:
      case ExpiringTargetsInstructionKind.REMOVE_EXPIRE_TARGET_DATE:
        let expiringContextTargetsChangeKind;
        if (semanticInstruction.kind === ExpiringTargetsInstructionKind.UPDATE_EXPIRE_TARGET_DATE) {
          expiringContextTargetsChangeKind = InstructionListChangeKind.CHANGE;
        } else if (semanticInstruction.kind === ExpiringTargetsInstructionKind.ADD_EXPIRE_TARGET_DATE) {
          expiringContextTargetsChangeKind = InstructionListChangeKind.ADD;
        } else if (semanticInstruction.kind === ExpiringTargetsInstructionKind.REMOVE_EXPIRE_TARGET_DATE) {
          expiringContextTargetsChangeKind = InstructionListChangeKind.REMOVE;
        }
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={expiringContextTargetsChangeKind}
              onExit={onExit}
              conflict={conflict}
            >
              <ExpiringTargetsInstructionEntry
                expiringIns={semanticInstruction as ContextTargetingExpirationInstruction}
                projKey={projKey}
              />
            </InstructionListItem>
          ),
        });
      case RuleInstructionKind.REORDER_RULES:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <NestedInstructionListItem
              subCategoryHeader={replaceRuleLabelText(InstructionListSubCategory.REORDER_RULES, ruleLabel)}
              isOrderedList
              showConflictLink
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.REORDER}
              key={instructionIndex}
              onExit={onExit}
            >
              {sortedRules.map((rule, i) => (
                <InstructionListItem
                  key={i}
                  showConflictLink={false}
                  onExit={onExit}
                  changeKind={InstructionListChangeKind.DECIMAL}
                >
                  <RuleInstructionDescription
                    projKey={projKey}
                    envKey={envKey}
                    kind={RuleDescriptionKind.REORDERED_RULE}
                    rule={rule}
                    showServeVariation
                    variations={flag.variations}
                  />
                </InstructionListItem>
              ))}
            </NestedInstructionListItem>
          ),
        });
      case RuleInstructionKind.REPLACE_RULES:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              {semanticInstruction.rules.length > 0 ? (
                <ul className={styles.instructionItemSubList}>
                  {semanticInstruction.rules.map((rule, i) => (
                    <li key={i}>
                      <RuleInstructionDescription
                        projKey={projKey}
                        envKey={envKey}
                        rule={rule}
                        showServeVariation
                        variations={flag.variations}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </InstructionListItem>
          ),
        });
      case RuleInstructionKind.ADD_RULE:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.ADD}
              onExit={onExit}
              conflict={conflict}
            >
              {semanticInstruction.progressiveRolloutConfiguration ? (
                <ProgressiveRolloutConfigurationDescription
                  variations={flag.variations.toArray()}
                  progressiveRolloutConfiguration={semanticInstruction.progressiveRolloutConfiguration}
                  rolloutContextKind={semanticInstruction.rolloutContextKind}
                  clauses={semanticInstruction.clauses.filter((c) => c !== null)}
                  projKey={projKey}
                  envKey={envKey}
                />
              ) : (
                <RuleInstructionDescription
                  projKey={projKey}
                  envKey={envKey}
                  rule={semanticInstruction}
                  showServeVariation
                  variations={flag.variations}
                />
              )}
            </InstructionListItem>
          ),
        });
      case RuleInstructionKind.REMOVE_RULE:
        const rule: Rule | undefined = flag.getRuleById(envKey, ruleId);
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.REMOVE}
              onExit={onExit}
              conflict={conflict}
            >
              {rule ? (
                <RuleInstructionDescription
                  projKey={projKey}
                  envKey={envKey}
                  rule={convertRuleToAggregateRule(rule, flag.variations)}
                  showServeVariation
                  variations={flag.variations}
                />
              ) : (
                <div>
                  <Alert variant="inline" status="warning">
                    <span>{`Rule ${ruleId} already removed`}</span>
                  </Alert>
                </div>
              )}
            </InstructionListItem>
          ),
          shouldAddElemToFront: true,
        });
      case ClauseInstructionKind.ADD_CLAUSES:
      case ClauseInstructionKind.UPDATE_CLAUSE:
        let clauseChangeKind;
        if (semanticInstruction.kind === ClauseInstructionKind.ADD_CLAUSES) {
          clauseChangeKind = InstructionListChangeKind.ADD;
        } else if (semanticInstruction.kind === ClauseInstructionKind.UPDATE_CLAUSE) {
          clauseChangeKind = InstructionListChangeKind.CHANGE;
        }
        return categorizeEditRuleInstruction({
          ruleId,
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={clauseChangeKind}
              onExit={onExit}
              conflict={conflict}
            >
              <RuleInstructionDescription
                projKey={projKey}
                envKey={envKey}
                rule={createAggregateRule({
                  ...semanticInstruction,
                  clauses:
                    semanticInstruction.kind === ClauseInstructionKind.ADD_CLAUSES
                      ? // @ts-expect-error clause value here is unknown[] when "createClause" expects ClauseValueType[]
                        semanticInstruction.clauses.map(createClause)
                      : undefined,
                })}
                variations={flag.variations}
              />
            </InstructionListItem>
          ),
        });
      case ClauseInstructionKind.ADD_VALUES_TO_CLAUSE:
      case ClauseInstructionKind.REMOVE_VALUES_FROM_CLAUSE:
        let clauseValueChangeKind;
        if (semanticInstruction.kind === ClauseInstructionKind.ADD_VALUES_TO_CLAUSE) {
          clauseValueChangeKind = InstructionListChangeKind.ADD;
        } else if (semanticInstruction.kind === ClauseInstructionKind.REMOVE_VALUES_FROM_CLAUSE) {
          clauseValueChangeKind = InstructionListChangeKind.REMOVE;
        }
        const clauseValueRule: Rule | undefined = flag.getRuleById(envKey, ruleId);
        const clause = clauseValueRule?.get('clauses').find((c: Clause) => c._id === semanticInstruction.clauseId);
        return categorizeEditRuleInstruction({
          ruleId,
          hasConflict,
          insElem: (
            <InstructionListItem
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={clauseValueChangeKind}
              onExit={onExit}
              conflict={conflict}
            >
              <ClauseValueInstructionEntry
                projKey={projKey}
                envKey={envKey}
                clause={clause || new Clause()}
                values={List(semanticInstruction.values)}
                kind={semanticInstruction.kind}
              />
            </InstructionListItem>
          ),
        });
      case ClauseInstructionKind.REMOVE_CLAUSES:
        const clauseRule: Rule | undefined = flag.getRuleById(envKey, ruleId);
        const removedClauses = clauseRule
          ?.get('clauses')
          .filter((d: Clause) => semanticInstruction.clauseIds.includes(d._id));
        return categorizeEditRuleInstruction({
          ruleId,
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.REMOVE}
              onExit={onExit}
              conflict={conflict}
            >
              <RuleInstructionDescription
                projKey={projKey}
                envKey={envKey}
                rule={clauseRule ? createAggregateRule({ clauses: removedClauses?.toArray() }) : clauseRule}
                variations={flag.variations}
              />
            </InstructionListItem>
          ),
        });
      case RuleInstructionKind.UPDATE_RULE_DESCRIPTION:
        const { description: ruleDescription } = semanticInstruction;
        return categorizeEditRuleInstruction({
          ruleId,
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              <span>
                to <span className="u-fw-semibold">{ruleDescription}</span>
              </span>
            </InstructionListItem>
          ),
        });
      case RuleInstructionKind.DISABLE_RULE:
      case RuleInstructionKind.ENABLE_RULE:
        return categorizeEditRuleInstruction({
          ruleId,
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            />
          ),
        });
      case RuleInstructionKind.STOP_MEASURED_ROLLOUT_ON_RULE:
        categoriesToExpand.push(FlagInstructionKindToInstructionListCategory[semanticInstruction.kind]);
        return categorizeEditRuleInstruction({
          ruleId,
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              <StopMeasuredRolloutInstructionEntry instruction={semanticInstruction} flag={flag} />
            </InstructionListItem>
          ),
        });
      case OnOffInstructionKind.STOP_MEASURED_ROLLOUT_ON_FALLTHROUGH:
        categoriesToExpand.push(FlagInstructionKindToInstructionListCategory[semanticInstruction.kind]);
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              <StopMeasuredRolloutInstructionEntry instruction={semanticInstruction} flag={flag} />
            </InstructionListItem>
          ),
        });
      case RuleInstructionKind.UPDATE_RULE_VARIATION_OR_ROLLOUT:
        return categorizeEditRuleInstruction({
          ruleId,
          hasConflict,
          insElem: (
            <InstructionListItem
              className={clsx({
                [styles.inlineInstructionListItem]: semanticInstruction.variationId !== undefined,
              })}
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              {semanticInstruction.progressiveRolloutConfiguration ? (
                <ProgressiveRolloutConfigurationDescription
                  variations={flag.variations.toArray()}
                  progressiveRolloutConfiguration={semanticInstruction.progressiveRolloutConfiguration}
                  rolloutContextKind={semanticInstruction.rolloutContextKind}
                />
              ) : (
                <VariationOrRolloutInstructionEntry flag={flag} instruction={semanticInstruction} />
              )}
            </InstructionListItem>
          ),
        });
      case OnOffInstructionKind.UPDATE_FALLTHROUGH_VARIATION_OR_ROLLOUT:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
              className={clsx({
                [styles.inlineInstructionListItem]: semanticInstruction.variationId !== undefined,
              })}
            >
              {semanticInstruction.progressiveRolloutConfiguration ? (
                <ProgressiveRolloutConfigurationDescription
                  variations={flag.variations.toArray()}
                  progressiveRolloutConfiguration={semanticInstruction.progressiveRolloutConfiguration}
                  rolloutContextKind={semanticInstruction.rolloutContextKind}
                />
              ) : (
                <VariationOrRolloutInstructionEntry flag={flag} instruction={semanticInstruction} />
              )}
            </InstructionListItem>
          ),
        });
      case OnOffInstructionKind.UPDATE_OFF_VARIATION:
        const { variationId: offVariationId } = semanticInstruction;
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={offVariationId === null ? 'Clear off variation' : description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              {offVariationId === null ? undefined : (
                <VariationInstructionEntry flag={flag} variationId={offVariationId} />
              )}
            </InstructionListItem>
          ),
        });
      case FlagVariationsInstructionKind.ADD_VARIATION:
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.ADD}
              onExit={onExit}
              conflict={conflict}
            >
              <AddVariationInstructionEntry
                variationType={flag.getVariationType()}
                value={stringifyValue(semanticInstruction.value)}
                name={semanticInstruction.name}
                description={semanticInstruction.description}
              />
            </InstructionListItem>
          ),
        });
      case FlagVariationsInstructionKind.REMOVE_VARIATION: {
        const variationFromFlag = flag.variations.get(flag.getVariationIndexFromId(semanticInstruction.variationId));
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.REMOVE}
              onExit={onExit}
              conflict={conflict}
            >
              <RemoveVariationInstructionEntry
                variationType={flag.getVariationType()}
                isVariationNotFound={!variationFromFlag}
                name={variationFromFlag?.name ?? variationFromFlag?.description ?? ''}
                value={variationFromFlag ? stringifyValue(variationFromFlag.value) : ''}
              />
            </InstructionListItem>
          ),
        });
      }
      case FlagVariationsInstructionKind.UPDATE_VARIATION: {
        const variationIndex = flag.getVariationIndexFromId(semanticInstruction.variationId);
        const variationFromFlag = variationIndex < 0 ? undefined : flag.variations.get(variationIndex);

        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              <UpdateVariationInstructionEntry
                variationType={flag.getVariationType()}
                currentVariationName={variationFromFlag?.name}
                isVariationNotFound={!variationFromFlag}
                name={
                  'name' in semanticInstruction
                    ? {
                        current: variationFromFlag?.name,
                        updated: semanticInstruction.name,
                      }
                    : undefined
                }
                description={
                  'description' in semanticInstruction
                    ? {
                        current: variationFromFlag?.description,
                        updated: semanticInstruction.description,
                      }
                    : undefined
                }
                value={
                  'value' in semanticInstruction
                    ? {
                        current: variationFromFlag ? stringifyValue(variationFromFlag.value) : '',
                        updated:
                          semanticInstruction.value !== undefined
                            ? stringifyValue(semanticInstruction.value)
                            : undefined,
                      }
                    : undefined
                }
              />
            </InstructionListItem>
          ),
        });
      }
      case FlagVariationsInstructionKind.UPDATE_DEFAULT_VARIATION:
        const onVariationInfo: VariationInfo = {
          index: -1,
          displayValue: '',
        };
        const offVariationInfo: VariationInfo = {
          index: -1,
          displayValue: '',
        };

        if (semanticInstruction.onVariationValue !== undefined) {
          const isVariationWithValueBeingRemoved = instructions.some(
            (ins) =>
              ins &&
              ins.kind === FlagVariationsInstructionKind.REMOVE_VARIATION &&
              flag.variations.some(
                (fv) => fv._id === ins.variationId && fv.value === semanticInstruction.onVariationValue,
              ),
          );
          const newVariationInstructionWithValue = instructions.find(
            (ins) =>
              ins &&
              ins.kind === FlagVariationsInstructionKind.ADD_VARIATION &&
              ins.value === semanticInstruction.onVariationValue,
          ) as AddVariationSemanticInstruction | undefined;

          if (isVariationWithValueBeingRemoved && newVariationInstructionWithValue) {
            onVariationInfo.displayValue =
              newVariationInstructionWithValue.name || stringifyValue(newVariationInstructionWithValue.value);
          } else {
            onVariationInfo.index = flag.getVariationIndexFromValue(semanticInstruction.onVariationValue);
            const existingVariation =
              onVariationInfo.index > -1 ? flag.variations.get(onVariationInfo.index) : undefined;
            onVariationInfo.displayValue =
              existingVariation?.getDisplay() || stringifyValue(semanticInstruction.onVariationValue);
          }
        }

        if (semanticInstruction.offVariationValue !== undefined) {
          const isVariationWithValueBeingRemoved = instructions.some(
            (ins) =>
              ins &&
              ins.kind === FlagVariationsInstructionKind.REMOVE_VARIATION &&
              flag.variations.some(
                (fv) => fv._id === ins.variationId && fv.value === semanticInstruction.offVariationValue,
              ),
          );
          const newVariationInstructionWithValue = instructions.find(
            (ins) =>
              ins &&
              ins.kind === FlagVariationsInstructionKind.ADD_VARIATION &&
              ins.value === semanticInstruction.offVariationValue,
          ) as AddVariationSemanticInstruction | undefined;

          if (isVariationWithValueBeingRemoved && newVariationInstructionWithValue) {
            offVariationInfo.displayValue =
              newVariationInstructionWithValue.name || stringifyValue(newVariationInstructionWithValue.value);
          } else {
            offVariationInfo.index = flag.getVariationIndexFromValue(semanticInstruction.offVariationValue);
            const existingVariation =
              offVariationInfo.index > -1 ? flag.variations.get(offVariationInfo.index) : undefined;
            offVariationInfo.displayValue =
              existingVariation?.getDisplay() || stringifyValue(semanticInstruction.offVariationValue);
          }
        }

        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              className={defaultVariationStyles.container}
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              <DefaultVariationInstructionEntry
                variationType={flag.getVariationType()}
                onVariation={semanticInstruction.onVariationValue !== undefined ? onVariationInfo : undefined}
                offVariation={semanticInstruction.offVariationValue !== undefined ? offVariationInfo : undefined}
              />
            </InstructionListItem>
          ),
        });
      case RuleInstructionKind.ADD_RULE_WITH_MEASURED_ROLLOUT_V2:
      case OnOffInstructionKind.UPDATE_FALLTHROUGH_WITH_MEASURED_ROLLOUT_V2:
      case RuleInstructionKind.UPDATE_RULE_WITH_MEASURED_ROLLOUT_V2: {
        categoriesToExpand.push(FlagInstructionKindToInstructionListCategory[semanticInstruction.kind]);

        const addRuleWithMeasuredRolloutInstruction =
          semanticInstruction.kind === RuleInstructionKind.ADD_RULE_WITH_MEASURED_ROLLOUT_V2;

        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.CHANGE}
              onExit={onExit}
              conflict={conflict}
            >
              {addRuleWithMeasuredRolloutInstruction && (
                <RuleInstructionDescription
                  projKey={projKey}
                  envKey={envKey}
                  rule={semanticInstruction as AggregateRule}
                  variations={flag.variations}
                />
              )}
              <MeasuredRolloutRuleInstructionDescription
                semanticInstruction={semanticInstruction}
                variations={flag.variations}
              />
            </InstructionListItem>
          ),
          shouldAddElemToFront: true,
        });
      }
      case ProgressiveRolloutInstructionKind.STOP_PROGRESSIVE_ROLLOUT: {
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              description={description}
              key={instructionIndex}
              changeKind={InstructionListChangeKind.REMOVE}
              onExit={onExit}
              conflict={conflict}
            >
              <StopProgressiveRolloutInstructionEntry
                flag={flag}
                environmentKey={envKey}
                ruleIdOrFallthrough={semanticInstruction.ruleId}
                endVariationId={semanticInstruction.endVariationId}
              />
            </InstructionListItem>
          ),
        });
      }
      case RuleInstructionKind.START_AUTOMATED_RELEASE: {
        return categorizeInstruction({
          category: FlagInstructionKindToInstructionListCategory[semanticInstruction.kind],
          hasConflict,
          insElem: (
            <InstructionListItem
              key={instructionIndex}
              conflictLink={conflictLink}
              changeKind={InstructionListChangeKind.ADD}
              onExit={onExit}
              conflict={conflict}
            >
              <StartAutomatedReleaseInstructionEntry
                flag={flag}
                projKey={projKey}
                environmentKey={envKey}
                instruction={semanticInstruction}
              />
            </InstructionListItem>
          ),
        });
      }
      case AutomatedReleaseInstructionKind.STOP_AUTOMATED_RELEASE: {
        const stopAutomatedReleaseElem = (
          <InstructionListItem
            description={description}
            key={instructionIndex}
            conflictLink={conflictLink}
            changeKind={InstructionListChangeKind.CHANGE}
            onExit={onExit}
            conflict={conflict}
          >
            <VariationInstructionEntry flag={flag} variationId={semanticInstruction.finalVariationId} />
          </InstructionListItem>
        );
        if (semanticInstruction.ruleId !== undefined) {
          categoriesToExpand.push(InstructionListCategory.RULES);
          return categorizeEditRuleInstruction({
            ruleId: semanticInstruction.ruleId,
            hasConflict,
            insElem: stopAutomatedReleaseElem,
          });
        }
        categoriesToExpand.push(InstructionListCategory.DEFAULT_RULE);
        return categorizeInstruction({
          category: InstructionListCategory.DEFAULT_RULE,
          hasConflict,
          insElem: stopAutomatedReleaseElem,
        });
      }
      default:
        return;
    }
  });

  Object.keys(editRuleInstructionElemsByRuleId).forEach((ruleId) => {
    if (editRuleInstructionElemsByRuleId[ruleId].length === 0) {
      return;
    }
    const ruleIdx = sortedRules.findIndex((rule) => rule && rule.ruleId === ruleId);
    const ruleNumber = ruleIdx + 1;
    const rule = sortedRules[ruleIdx];
    let ruleDescription = rule ? rule.description || `${capitalize(ruleLabel)} ${ruleNumber}` : RULE_NOT_FOUND;
    if (rule?.experimentAllocation && rule?.experimentAllocation?.type !== 'measuredRollout') {
      ruleDescription = 'experiment';
    }
    const insElem = (
      <NestedInstructionListItem
        subCategoryHeader={replaceRuleLabelText(InstructionListSubCategory.EDIT_RULE, ruleLabel)}
        subCategoryDetails={ruleDescription}
        changeKind={InstructionListChangeKind.CHANGE}
        onExit={onExit}
        key={ruleId}
      >
        {editRuleInstructionElemsByRuleId[ruleId].map((ins) => ins)}
      </NestedInstructionListItem>
    );

    categorizeInstruction({ category: InstructionListCategory.RULES, insElem, hasConflict: false });
  });

  return <ul className={styles.InstructionList}>{renderInstructionsWithCategories(categoriesToExpand)}</ul>;
}
